import { useState, useEffect } from 'react';

import type { OperationVariables } from '../../types';

type AppImages = {
  url_128x128: string;
  url_64x64: string;
  url_32x32: string;
  url_16x16: string;
};

type PreTransformedApp = {
  images: AppImages;
  id: string;
  canonical_id: string;
  current_implementation_id: string;
  name: string;
  slug: string;
};

type App = {
  id: string;
  canonicalId: string;
  serviceId: string;
  slug: string;
  name: string;
  images: AppImages;
  currentImplementation: {
    id: string;
  };
};

type options = {
  /** limit */
  limit?: number;
  /** searchDebounceDelay */
  searchDebounceDelay?: number;
  /** Excludes upcoming apps from search results */
  excludeUpcoming?: boolean;
  /** Toggles the inclusion of services where the authenticated user is associated with an app as an admin */
  includeIsAppAdmin?: boolean;
  /** Toggles the inclusion of services where the authenticated user is associated with an app as a collaborator */
  includeIsAppCollaborator?: boolean;
  /**
   * Force API requests to be sent to this hostname, instead of the current domain.
   * Most likely needed for applications running on a subdomain.
   */
  apiHostname?: string;
  /** Optional callback function to be invoked when the search results are updated. */
  onResultsUpdate?: (searchQuery: string, results: any) => void;
};

type useAppSearchDataOutput = OperationVariables & {
  onSearch: (query: string) => void;
  searchQuery: string;
  apps: App[];
};

const DEFAULT_LIMIT = 20;

const timeoutIds = new WeakMap();
const simpleDebounce =
  (fn: Function, delay: number) =>
  (...args: any) => {
    clearTimeout(timeoutIds.get(fn));
    const timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
    timeoutIds.set(fn, timeoutId);
  };

const dedupeAndTrimWhitespace = (query: string) =>
  query.replace(/(\s+)/g, ' ').trim();

/**
 * Provides the ability to search for Zapier apps (services, integrations)
 * and receive a set of matching results.
 */
export const useAppSearchData = ({
  limit,
  searchDebounceDelay,
  excludeUpcoming,
  includeIsAppAdmin,
  includeIsAppCollaborator,
  apiHostname,
  onResultsUpdate,
}: options): useAppSearchDataOutput => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const searchQueryTrimmed = dedupeAndTrimWhitespace(searchQuery);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<App[]>([]);
  useEffect(() => {
    if (searchQueryTrimmed === '') {
      setData([]);
      return;
    }

    async function fetchAppData() {
      const searchParams = new URLSearchParams();
      searchParams.append('limit', (limit || DEFAULT_LIMIT).toString());
      searchParams.append('title_search', searchQueryTrimmed);
      if (excludeUpcoming)
        searchParams.append('exclude_upcoming', excludeUpcoming.toString());
      if (includeIsAppAdmin)
        searchParams.append(
          'including_is_app_admin',
          includeIsAppAdmin.toString()
        );
      if (includeIsAppCollaborator)
        searchParams.append(
          'including_is_app_collaborator',
          includeIsAppCollaborator.toString()
        );

      try {
        setLoading(true);
        const response = await fetch(
          `${apiHostname}/api/v4/apps?${searchParams.toString()}`
        );
        if (!response.ok) {
          throw new Error('Response was not OK');
        }
        const preTransformedAppsResponse = await response.json();
        const edges = preTransformedAppsResponse.results.map(
          (app: PreTransformedApp) => {
            return {
              id: app.id,
              canonicalId: app.canonical_id,
              slug: app.slug,
              name: app.name,
              currentImplementation: {
                id: app.current_implementation_id,
              },
              images: app.images,
            };
          }
        );

        setData(edges);
        if (onResultsUpdate) {
          onResultsUpdate(searchQuery, edges);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
    fetchAppData();
  }, [
    excludeUpcoming,
    includeIsAppAdmin,
    includeIsAppCollaborator,
    limit,
    searchQueryTrimmed,
  ]);

  return {
    loading,
    onSearch: simpleDebounce((query: string) => {
      setSearchQuery(dedupeAndTrimWhitespace(query));
    }, searchDebounceDelay || 0),
    searchQuery: dedupeAndTrimWhitespace(searchQuery),
    apps: data,
  };
};
